'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { DeferredCardCartButton } from './deferred-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { Heading } from '../../ui/heading';
import { ProductColors } from '../product-colors';
import { ProductComparisonButton } from '../product-comparison-button';
import { ProductBadge } from '../detail/product-badge';

export type ProductCardProps = {
  product: Product;
  imageSizes?: string;
  imagePriority?: boolean;
  headingTag?: 'h2' | 'h3';
};

function ProductCardInternal({
  product,
  imageSizes,
  imagePriority = false,
  headingTag = 'h3',
}: ProductCardProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filter = useCategoryStore(state => state.filter);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  // Cards have no variant selector. Variant selection remains in the intent-loaded cart dialog.
  const productUrl = getProductUrl(product, []);

  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, { isWholesaler });

  const showAvailability = !!product.availability;

  return (
    <div
      className={cn(
        'group relative isolate',
        'col-span-1 flex w-full cursor-pointer flex-col items-start justify-start rounded-[12px] bg-[#F9FAFB] transition-all ease-in-out hover:bg-white',
      )}
    >
      {/* One real link covers image, title, price and whitespace. Commerce controls
          are separate siblings, never nested inside the navigation anchor. */}
      <Link
        prefetch={false}
        prefetchOnHover
        href={productUrl}
        aria-label={product.name}
        className="absolute inset-0 z-0 rounded-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      />
      <div className="pointer-events-none w-full px-[6px] py-[6px] md:px-4 md:py-3">
        <div className="relative aspect-square overflow-hidden">
          <div className="absolute left-1/2 top-3 z-10 flex w-[calc(100%-24px)] -translate-x-1/2 items-start justify-between">
            <div className="flex flex-col items-start justify-start gap-2">
              {showAvailability && (
                <ProductBadge
                  text={product.availability?.name || ''}
                  background={product.availability?.color || '#E0E0E0'}
                  color={getContrastYIQ(product.availability?.color || '#E0E0E0')}
                />
              )}
              {priceDisplay.showPreviousPrice && (
                <ProductBadge
                  text={`-${priceDisplay.discountLabel}%`}
                  background="#FFF06F"
                  color="#000000"
                />
              )}
              {product.tags?.map(tag => (
                <ProductBadge
                  key={tag.name}
                  text={tag.name}
                  background={tag.color ?? '#E0E0E0'}
                  color={getContrastYIQ(tag.color ?? '#E0E0E0')}
                />
              ))}
            </div>
            <AddToFavoritesButton
              buttonSize="icon-lg"
              slug={product.slug}
              buttonVariant={'white'}
              isFavorite={product.is_favorite ?? false}
              buttonClassName={cn('pointer-events-auto ea-product-card__favorites-button')}
              productId={product.id}
            />
          </div>
          <div
            className={cn(
              'absolute bottom-3 left-1/2 z-10 flex w-[calc(100%_-_32px)] -translate-x-1/2 translate-y-[100px] items-center justify-end gap-2 sm:justify-between',
              'transition-all ease-in-out group-hover:translate-y-0',
            )}
          >
            <DeferredCardCartButton
              product={product}
              showIcon={!!templateSettings.product_teaser?.cart_icon}
              sellable={isSellable(product, 1)}
              className={cn(
                'pointer-events-auto w-full flex-1',
                'ea-product-card__add-to-cart-button',
              )}
            />
            <div className="pointer-events-auto">
              <ProductComparisonButton product={product} shadow />
            </div>
          </div>
          <div className="absolute inset-0 z-0 rounded-[12px]">
            <Image
              src={image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(image, product?.name)}
              width={0}
              height={0}
              loading={imagePriority ? 'eager' : undefined}
              fetchPriority={imagePriority ? 'high' : undefined}
              className="h-full w-full rounded-[12px] bg-white object-contain object-center p-0.5 md:p-1"
              sizes={
                imageSizes ??
                '(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw'
              }
            />
          </div>
        </div>
      </div>

      <div className="pointer-events-none flex w-full flex-col items-start justify-start gap-3 p-3 md:gap-4 md:p-4">
        <Heading
          title={product.name}
          tag={headingTag}
          styleAs="h5"
          className={{
            title: cn(
              'w-full group-hover:text-primary',
              'line-clamp-2 h-[47px] transition-all ease-in-out',
            ),
            container: 'w-full',
          }}
        />
        <ProductPrice product={product} />
        <ProductColors product={product} />
      </div>
    </div>
  );
}

// Parent query/carousel updates reuse unchanged product objects. Store selectors
// inside the card remain live (customer, filter, prices and favorites).
export const ProductCard = React.memo(ProductCardInternal);
